'use client';

import { useEffect, useId, useMemo, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { WikiLink as Link } from './WikiLink';
import { useRouter } from 'next/navigation';
import { Button } from '../../../components/ui/Button';
import { FiX, RiSearchLine } from '../../../components/icons';
import { useApiClient } from '../../../contexts/ApiContext';
import { cn } from '../../../utils/cn';
import { WIKI_MIN_QUERY_LENGTH, wikiHref, wikiUiText } from '../wikiText';
import type { WikiLang, WikiSearchHit } from '../wiki.types';
import { WikiHitRow, wikiHitHref } from './WikiHitRow';
import { useWikiGoal } from './useWikiGoal';

const MIN_QUERY_LENGTH = WIKI_MIN_QUERY_LENGTH;
const MAX_SUGGESTIONS = 8;
const DEBOUNCE_MS = 250;

interface WikiSearchBoxProps {
  lang: WikiLang;
  defaultQuery?: string;
  className?: string;
}

export const WikiSearchBox: FC<WikiSearchBoxProps> = ({ lang, defaultQuery = '', className }) => {
  const t = useMemo(() => wikiUiText(lang), [lang]);
  const apiClient = useApiClient();
  const router = useRouter();
  const reachGoal = useWikiGoal();
  const listId = useId();
  const [query, setQuery] = useState(defaultQuery);
  const [hits, setHits] = useState<WikiSearchHit[] | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const latestRequest = useRef(0);

  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setHits(null);
      return;
    }
    const requestId = ++latestRequest.current;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      apiClient.api
        .wikiControllerSuggestWiki({ lang, q: trimmed }, { signal: controller.signal })
        .then((result) => {
          if (requestId !== latestRequest.current) return;
          setHits(result.slice(0, MAX_SUGGESTIONS));
          setActive(-1);
        })
        .catch(() => {
          if (requestId === latestRequest.current && !controller.signal.aborted) setHits(null);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [apiClient, lang, trimmed]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const showList = open && hits !== null && trimmed.length >= MIN_QUERY_LENGTH;
  const searchHref = wikiHref(lang, `/wiki/search?q=${encodeURIComponent(trimmed)}`);

  const pick = () => {
    setOpen(false);
    reachGoal('wiki_search');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (!hits || hits.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % hits.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (i <= 0 ? hits.length - 1 : i - 1));
    } else if (event.key === 'Enter' && showList && active >= 0) {
      const hit = hits[active];
      if (hit) {
        event.preventDefault();
        pick();
        router.push(wikiHitHref(lang, hit));
      }
    }
  };

  return (
    <form
      ref={rootRef}
      role="search"
      method="get"
      action={wikiHref(lang, '/wiki/search')}
      onSubmit={() => reachGoal('wiki_search')}
      className={cn('relative w-full', className)}
    >
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <RiSearchLine
            aria-hidden
            size={18}
            className="pointer-events-none absolute start-3.5 top-1/2 hidden -translate-y-1/2 text-label-tertiary sm:block"
          />
          <input
            ref={inputRef}
            type="search"
            name="q"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={t('searchPlaceholderShort')}
            aria-label={t('searchPlaceholder')}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={listId}
            aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
            autoComplete="off"
            minLength={MIN_QUERY_LENGTH}
            maxLength={100}
            className={cn(
              'h-12 w-full rounded-xl border border-border-subtle bg-surface-input ps-4 text-[16px] text-label-primary placeholder:text-subtle focus:border-accent-blue focus:outline-none sm:ps-10 sm:pe-12 [&::-webkit-search-cancel-button]:hidden',
              query ? 'pe-[88px]' : 'pe-12',
            )}
          />
          {query && (
            <Button
              type="button"
              variant="tertiary"
              size="s"
              aria-label={t('searchClear')}
              onClick={() => {
                setQuery('');
                setHits(null);
                inputRef.current?.focus();
              }}
              className="absolute end-11 top-1/2 size-10 -translate-y-1/2 bg-transparent p-0 text-label-secondary hover:text-label-primary sm:end-1"
            >
              <FiX aria-hidden size={18} />
            </Button>
          )}
          <Button
            type="submit"
            variant="tertiary"
            size="s"
            aria-label={t('searchSubmit')}
            disabled={trimmed.length < MIN_QUERY_LENGTH}
            className="absolute end-1 top-1/2 size-10 -translate-y-1/2 bg-transparent p-0 text-label-primary sm:hidden"
          >
            <RiSearchLine aria-hidden size={20} />
          </Button>
        </div>
        <Button type="submit" size="l" disabled={trimmed.length < MIN_QUERY_LENGTH} className="hidden h-12 shrink-0 py-0 sm:inline-flex">
          {t('searchSubmit')}
        </Button>
      </div>
      {showList && (
        <div className="absolute inset-x-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-[18px] border border-white/[0.12] bg-bg-secondary p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.75)]">
          {hits.length === 0 ? (
            <p className="m-0 px-3 py-3 text-[14px] text-label-secondary">{t('suggestEmpty')}</p>
          ) : (
            <ul id={listId} role="listbox" className="m-0 flex list-none flex-col p-0">
              {hits.map((hit, i) => (
                <li key={`${hit.type}-${hit.slug}`} id={`${listId}-${i}`} role="option" aria-selected={i === active}>
                  <Link href={wikiHitHref(lang, hit)} onMouseEnter={() => setActive(i)} onClick={pick} className="block">
                    <WikiHitRow hit={hit} t={t} active={i === active} compact />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link href={searchHref} onClick={pick} className="mt-1 block rounded-xl px-3 py-2.5 text-[14px] font-medium text-accent-blue hover:bg-white/[0.06]">
            {t('suggestAll')}
          </Link>
        </div>
      )}
    </form>
  );
};
